import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  View,
  Text,
  Image,
  Dimensions,
  StyleSheet,
  Pressable,
  Animated,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { MaterialIcons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import type { LayoutChangeEvent } from 'react-native';
import { HeaderWithMenu } from '../components/menu';

import activelyChargingIcon from '../../assets/activelyChargingIcon.png';
import connectedNotCharging from '../../assets/connectedNotCharging.png';
import notConnectedOrCharging from '../../assets/notConnectedOrCharging.png';
import scheduledChargeIcon from '../../assets/scheduleChargeIcon.png';

import {
  checkinCharger,
  CheckinResponse,
  getActiveChargeData,
  registerLiveActivityToken,
  unregisterLiveActivityToken,
} from '../services/charger-service';
import { notificationService } from '../services/notification-service';
import { updateSelectedCharger } from '../store/actions/charging-actions';
import { chargerActions } from '../store/slices/charger-slice';
import { ActiveChargeStatus, ChargerAttributes, ChargerStateInfo } from '../models/charger-model';
import ChargerStatusDetailModal from '../components/ChargerStatusDetailModal';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  getCurrentUtcSeconds,
  secondsToHHMMSS,
} from '../components/common/utils/date-time-conversions';
import { checkIsPeak } from '../components/common/utils/peak-off-peak-field';
import { AppDispatch } from '../store';
import { getActiveCharges } from '../store/actions/user-actions';
import { userActions } from '../store/slices/user-slice';
import { ActiveChargeAttributes } from '../models/active-charges-model';
import { isSuccessful, fetchDataFromAPITyped } from '../services/api-requests';
import { getOrderById } from '../services/orders-service';
import LoadingButton from '../components/LoadingButton';
import SlideToActivateButton from '../components/SlideToActivateButton';
import Scheduler from './Scheduler';
import { routeTitleMap } from '../components/common/utils/route-title-map';
import {
  ChargerSchedule,
  deleteSchedule,
  SendSchedule,
  setSchedule,
} from '../store/actions/scheduling-actions';
import { Modal } from 'react-native-paper';
import LoadingSpinnerOverlay from '../components/LoadingSpinnerOverlay';
import { liveActivityService, calculateChargingRateKw } from '../services/LiveActivityService';
import { androidChargingNotificationService } from '../services/AndroidChargingNotificationService';
import UnavailableWarningBanner from '../components/UnavailableWarningBanner';
import type { UnavailableWarning, DriverUnavailableChoice } from '../models/charger-model';

const { width, height: windowHeight } = Dimensions.get('window');
const MIN_IMAGE_HEIGHT = 80;
const MAX_IMAGE_HEIGHT = 280;
const CAR_THRESHOLD = 100; // Lowered to show car more often
const ESTIMATED_CONTENT_HEIGHT_BELOW_IMAGE = 420;

const ChargingState = {
  CONNECTED_AND_CHARGING: 0,
  CONNECTED_AND_NOT_CHARGING: 1,
  DISCONNECTED: 3,
  SCHEDULED_CONNECTED: 4,
  SCHEDULED_NOT_CONNECTED: 5,
  FAULTED: 6,
  UNKNOWN: -1,
};

const TAB_BAR_HEIGHT = 0; // No footer anymore
const SAFE_AREA_PADDING = 20; // Minimal padding for safe area

// A session is free when the order was created with a free override (frozen
// at order creation) or the live payload's pricing override resolves to $0.
// Checked in both places because some payloads omit pricing_override, and the
// backend's peakPrice/offPeakPrice/estimatedCost don't honor the override.
const isFreeChargeSession = (
  override?: ActiveChargeStatus['pricing_override'],
  order?: { is_free_override?: boolean; pricing_override_discount_type?: string | null } | null
): boolean =>
  order?.is_free_override === true ||
  order?.pricing_override_discount_type === 'free' ||
  (!!override &&
    (override.discount_type === 'free' ||
      (override.effective_price_peak === 0 && override.effective_price_off_peak === 0)));

export default function ChargingScreen(props: { navigation: any; route: any }) {
  const insets = useSafeAreaInsets();
  const [timeElapsed, setTimeElapsed] = useState<number>(0);
  const [localTimer, setLocalTimer] = useState<number>(0);
  const [kWhCharged, setKWhCharged] = useState<number>(0);
  const [kwhDisplayValue, setKwhDisplayValue] = useState(kWhCharged);
  const [estPrice, setEstPrice] = useState<number>(0);
  const [chargingStatus, setChargingStatus] = useState(ChargingState.UNKNOWN);
  const [activeChargeStatus, setActiveChargeStatus] =
    useState<ActiveChargeStatus>();

  const [scheduleStartTime, setScheduleStartTime] = useState<Date>(
    props.route.params.scheduleObj
      ? new Date(props.route.params.scheduleObj.startTime)
      : new Date()
  );
  const [scheduleEndTime, setScheduleEndTime] = useState<Date>(
    props.route.params.scheduleObj
      ? new Date(props.route.params.scheduleObj.endTime)
      : new Date()
  );
  const [selectedMaxKwh, setSelectedMaxKwh] = useState<number | null>(
    props.route.params.scheduleObj?.max_kwh ?? null
  );
  const [scheduleSet, setScheduleSet] = useState<boolean>(
    props.route.params.scheduleObj ? true : false
  );
  const [scheduleObj, setScheduleObj] = useState<ChargerSchedule | null>(
    props.route.params.scheduleObj
  );
  const dispatch: AppDispatch = useDispatch();

  const pollTimer = useRef<NodeJS.Timeout | null>(null);
  const lastServerUpdate = useRef<number>(0);
  let timer: NodeJS.Timeout | undefined;

  // Baseline accumulation for fault recovery and navigation resilience.
  // When the backend starts a new OCPP transaction after a fault, raw server
  // values reset to 0. The baseline captures previously accumulated totals so
  // the user sees continuous, non-decreasing metrics.
  const baselineTimer = useRef<number>(0);
  const baselineKwh = useRef<number>(0);
  const baselinePrice = useRef<number>(0);
  const lastKnownServerTime = useRef<number>(0);
  const lastKnownServerKwh = useRef<number>(0);
  const lastKnownServerPrice = useRef<number>(0);
  const metricsRestoredFromRedux = useRef<boolean>(false);

  // True once the metrics have been seeded from real server usageData at
  // least once this mount. Distinct from metricsHydrated, which also flips on
  // payloads WITHOUT usage data — relying on it caused a remount (app reopen,
  // Live Activity tap) to keep showing zeros because the first real usage
  // payload was then throttled/gated instead of applied immediately.
  const hasSeededUsageFromServer = useRef<boolean>(false);

  // False until the metrics have been seeded from a trustworthy source —
  // either the Redux session-metrics cache or the first getActiveChargeData
  // response. While false, the time/kWh/cost values render dimmed so a screen
  // remount (active charge card, app reopen) never flashes misleading zeros.
  const [metricsHydrated, setMetricsHydrated] = useState<boolean>(false);

  const selectedCharger: ChargerAttributes = useSelector(
    (state: RootState) => state.chargers.selectedCharger
  ) as ChargerAttributes;

  const activeCharges: ActiveChargeAttributes[] | null = useSelector(
    (state: RootState) => state.user.activeCharges
  );
  const suppressChargeSummaryNavigation: boolean = useSelector(
    (state: RootState) => state.user.suppressChargeSummaryNavigation
  );
  const chargingSessionMetrics = useSelector(
    (state: RootState) => state.chargers.chargingSessionMetrics
  );
  const [localActiveCharge, setLocalActiveCharge] =
    useState<ActiveChargeAttributes | null>(null);

  const startTime = useRef<Date>(new Date()); //useRef to persist value across renders
  const colorAnimation = useRef(new Animated.Value(0)).current;
  const pulseRef = useRef<Animated.CompositeAnimation | null>(null);
  const fadeAnimation = useRef(new Animated.Value(1)).current; // Animation for fade effect
  const [showCancelLoader, setShowCancelLoader] = useState<boolean>(false);
  const [belowImageHeight, setBelowImageHeight] = useState(0);
  const [isWaitingForCharge, setIsWaitingForCharge] = useState<boolean>(false);
  const [hasCheckedForCharge, setHasCheckedForCharge] =
    useState<boolean>(false);
  const [faultModalVisible, setFaultModalVisible] = useState<boolean>(false);
  const [notChargingModalVisible, setNotChargingModalVisible] = useState<boolean>(false);
  const [wifiDisconnectedModalVisible, setWifiDisconnectedModalVisible] = useState<boolean>(false);
  const [chargerWifiConnected, setChargerWifiConnected] = useState<boolean>(true);
  const [dataIsStale, setDataIsStale] = useState<boolean>(false);
  const [offlineSince, setOfflineSince] = useState<Date | null>(null);
  const [lastDataUpdateTime, setLastDataUpdateTime] = useState<Date | null>(null);
  const [currentChargerState, setCurrentChargerState] = useState<ChargerStateInfo | null>(null);

  // Unavailable window state
  const [unavailWarning, setUnavailWarning] = useState<UnavailableWarning | null>(null);
  const [pausedForUnavailable, setPausedForUnavailable] = useState(false);
  const [resumesAt, setResumesAt] = useState<string | null>(null);
  const [driverUnavailChoice, setDriverUnavailChoice] = useState<DriverUnavailableChoice>(null);

  const handleUnavailableChoice = useCallback(async (choice: 'pause_and_resume' | 'end_charge') => {
    setDriverUnavailChoice(choice);
    const orderId = localActiveCharge?.order?.id;
    const chargerId = selectedCharger?.uuid;
    if (!orderId || !chargerId) return;
    try {
      await fetchDataFromAPITyped<{ acknowledged: boolean }>({
        route: 'charger/unavailable/preference',
        request: {
          method: 'POST',
          entity: 'charger',
          body: { orderId, chargerId, action: choice },
        },
      });
    } catch (e) {
      console.error('[ChargingScreen] Failed to send unavailable preference:', e);
    }
  }, [localActiveCharge?.order?.id, selectedCharger?.uuid]);
  const [dynamicImageHeight, setDynamicImageHeight] = useState<number>(
    Math.max(
      MIN_IMAGE_HEIGHT,
      Math.min(
        MAX_IMAGE_HEIGHT,
        windowHeight - ESTIMATED_CONTENT_HEIGHT_BELOW_IMAGE
      )
    )
  );
  
  // Live Activity state for Dynamic Island
  const [liveActivityStarted, setLiveActivityStarted] = useState<boolean>(false);
  const liveActivityInitialized = useRef<boolean>(false);

  const handleBelowImageLayout = (event: LayoutChangeEvent) => {
    const measuredHeight = event.nativeEvent.layout.height;
    setBelowImageHeight(measuredHeight);
    
    // Calculate if car should be shown based on current image height
    const wouldShowCar = dynamicImageHeight > CAR_THRESHOLD;
    
    // Calculate available space for image, accounting for car, location block, and safe area
    const carAndLocationHeight = wouldShowCar ? carHeight + locationBlockHeight + 56 : locationBlockHeight + 16;
    const availableSpace = windowHeight - measuredHeight - carAndLocationHeight - TAB_BAR_HEIGHT - SAFE_AREA_PADDING;
    
    // Update image height to ensure content fits
    const newImageHeight = Math.max(
      MIN_IMAGE_HEIGHT,
      Math.min(MAX_IMAGE_HEIGHT, availableSpace)
    );
    
    if (newImageHeight !== dynamicImageHeight) {
      setDynamicImageHeight(newImageHeight);
    }
  };

  const imageHeight = dynamicImageHeight;
  const showCar = imageHeight > CAR_THRESHOLD;
  const carHeight = 74;
  const locationBlockHeight = 40;
  const showCarAndLocation = imageHeight > carHeight + locationBlockHeight + 40;

  useEffect(() => {
    if (!scheduleStartTime) return;

    const start = new Date(scheduleStartTime).getTime();
    const now = Date.now();
    const delay = start - now;

    if (delay <= 0) {
      // If the scheduled time has already passed, update immediately.
      setScheduleSet(false);
      return;
    }

    const timerId = setTimeout(() => {
      setScheduleSet(false);
    }, delay);

    // Clean up the timer if scheduleStartTime changes or the component unmounts.
    return () => clearTimeout(timerId);
  }, [scheduleStartTime]);

  const setScheduleTimes = async (
    startTime: Date,
    endTime?: Date | null,
    maxKwh?: number | null
  ) => {
    setScheduleStartTime(startTime);
    if (endTime) {
      setScheduleEndTime(endTime);
    } else {
      setScheduleEndTime(
        new Date(scheduleStartTime.getTime() + 12 * 60 * 60 * 1000)
      );
    }
    setSelectedMaxKwh(maxKwh ?? null);
    // A future start time means the charge is scheduled and waiting; a start
    // time of now (or in the past) means charging begins immediately.
    if (startTime > new Date()) {
      setScheduleSet(true);
    } else {
      setScheduleSet(false);
    }

    //reset the schedule within the same order
    if (selectedCharger && selectedCharger.uuid && localActiveCharge) {
      // Skip /schedule/set when the charger doesn't support scheduling
      // (host disabled it or charger is OCPP 1.6 / Emporia). Backend would
      // ignore the call anyway, so save the round trip.
      if (!selectedCharger.can_schedule) {
        return;
      }
      const sendSchedule: SendSchedule = {
        start_time: startTime.toISOString(),
        end_time:
          endTime?.toISOString() ??
          new Date(startTime.getTime() + 12 * 60 * 60 * 1000).toISOString(),
        chargerId: selectedCharger.uuid,
        orderId: props.route.params.order.id,
        max_kwh: maxKwh,
      };
      const scheduleResp = await setSchedule(sendSchedule);
      let scheduleData;
      if (Array.isArray(scheduleResp)) {
        scheduleData = scheduleResp[0];
      } else {
        scheduleData = scheduleResp;
      }
      // Only store a real schedule. On failure setSchedule returns the error
      // payload (no scheduleId) and already surfaced the specific reason, so
      // don't overwrite the current schedule with it.
      if (scheduleData && scheduleData.scheduleId) setScheduleObj(scheduleData);
    } else {
      notificationService.sendNotification(`error`, 'Error changing schedule');
    }
  };

  const clearSchedule = async () => {
    const d = new Date();
    const ed = new Date(d.getTime() + 12 * 60 * 60 * 1000);
    setScheduleStartTime(d);
    setScheduleEndTime(ed);

    //remove the set schedule and begin charging immediately
    //await deleteSchedule (scheduleObj.scheduleId);
    await setScheduleTimes(d, ed, selectedMaxKwh);
  };

  useEffect(() => {
    if (chargingStatus === ChargingState.CONNECTED_AND_CHARGING) {
      // Start the pulsing animation
      pulseRef.current = Animated.loop(
        Animated.sequence([
          Animated.timing(colorAnimation, {
            toValue: 1,
            duration: 1000,
            useNativeDriver: false,
          }),
          Animated.timing(colorAnimation, {
            toValue: 0,
            duration: 1000,
            useNativeDriver: false,
          }),
        ])
      );
      pulseRef.current.start();
    } else {
      // Stop the pulsing animation
      if (pulseRef.current) {
        pulseRef.current.stop();
        colorAnimation.setValue(0); // Reset color to initial state
      }
    }

    // Cleanup on unmount
    return () => {
      if (pulseRef.current) {
        pulseRef.current.stop();
      }
    };
  }, [colorAnimation, chargingStatus]);

  const interpolatedColor = colorAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: ['#FFFFFF', '#39BEB7'],
  });

  useEffect(() => {
    // Start fade-out animation
    Animated.timing(fadeAnimation, {
      toValue: 0, // Fade out to 0
      duration: 500, // Duration of fade-out
      useNativeDriver: true,
    }).start(() => {
      // After fade-out completes, update the display value and fade back in
      setKwhDisplayValue(kWhCharged);
      Animated.timing(fadeAnimation, {
        toValue: 1, // Fade in to 1
        duration: 500, // Duration of fade-in
        useNativeDriver: true,
      }).start();
    });
  }, [kWhCharged]); // Run this effect when kWhCharged changes

  const pollChargingStatus = async (orderId: string) => {
    return await getActiveChargeData(orderId);
  };

  const resolveReconciliationStatus = async (orderId: string): Promise<boolean> => {
    try {
      const orderResponse = await getOrderById(orderId);
      if (isSuccessful(orderResponse.status) && orderResponse.payload) {
        return orderResponse.payload.status === 'PENDING_RECONCILIATION';
      }
    } catch (err) {
      console.error('[ChargingScreen] Error checking order status after checkin failure:', err);
    }
    return false;
  };

  const handleChargingDone = async (orderId: string) => {
    // CRITICAL: Clear polling immediately to prevent any race conditions
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }

    if (timer) {
      clearInterval(timer);
    }

    // End Live Activity (iOS) or Notification (Android)
    // Wrap in comprehensive try/catch to prevent crashes from native module failures
    if (liveActivityStarted) {
      try {
        if (Platform.OS === 'ios') {
          try {
            await liveActivityService.endActivity(kWhCharged, estPrice, localTimer);
            console.log('[ChargingScreen] Live Activity ended');
          } catch (liveActivityError) {
            // Don't crash the app if Live Activity fails - log and continue
            console.warn('[ChargingScreen] Error ending Live Activity (non-fatal):', liveActivityError);
          }
          
          // Unregister token from backend - this is fire-and-forget
          unregisterLiveActivityToken(orderId).catch((unregError) => {
            console.warn('[ChargingScreen] Error unregistering Live Activity token:', unregError);
          });
        }
        
        if (Platform.OS === 'android') {
          try {
            // Show completion summary then dismiss
            await androidChargingNotificationService.endWithSummary(kWhCharged, estPrice, localTimer);
            console.log('[ChargingScreen] Android notification ended with summary');
          } catch (androidError) {
            console.warn('[ChargingScreen] Error ending Android notification (non-fatal):', androidError);
          }
        }
        
        setLiveActivityStarted(false);
        liveActivityStartedRef.current = false;
        liveActivityInitialized.current = false;
      } catch (error) {
        // Catch-all for any unexpected errors in the Live Activity cleanup
        console.error('[ChargingScreen] Unexpected error ending charging UI:', error);
        // Reset state anyway to prevent stuck state
        setLiveActivityStarted(false);
        liveActivityStartedRef.current = false;
        liveActivityInitialized.current = false;
      }
    }
    
    let isPendingReconciliation = false;
    try {
      const response = await checkinCharger(orderId);
      if (isSuccessful(response.status)) {
        const checkinData = response.payload as CheckinResponse | null;
        isPendingReconciliation = checkinData?.pendingReconciliation === true;

        if (isPendingReconciliation) {
          notificationService.sendNotification(
            'info',
            checkinData?.message ?? 'Your session has ended. Final charges will be updated once the charger reconnects.'
          );
        } else {
          notificationService.sendNotification(
            'success',
            'Charger successfully checked in!'
          );
        }
      } else {
        const errorPayload = response.payload as any;
        const errorMsg = errorPayload?.message || 'Error checking in your charger!';
        console.warn('[ChargingScreen] Checkin returned non-success status:', response.status, errorMsg);
        isPendingReconciliation = await resolveReconciliationStatus(orderId);
        if (!isPendingReconciliation) {
          notificationService.sendNotification('success', 'Your charging session is complete!');
        } else {
          notificationService.sendNotification('info', errorMsg);
        }
      }
    } catch (e) {
      console.error('[ChargingScreen] Error in checkinCharger:', e);
      isPendingReconciliation = await resolveReconciliationStatus(orderId);
      if (isPendingReconciliation) {
        notificationService.sendNotification(
          'info',
          'Your session is ending. If the charger is offline, final charges will be updated once it reconnects.'
        );
      } else {
        notificationService.sendNotification('success', 'Your charging session is complete!');
      }
    } finally {
      setShowCancelLoader(false);
    }

    setLocalActiveCharge(null);
    updateSelectedChargerAndNavigateToSummary(orderId, isPendingReconciliation);
  };

  const updateSelectedChargerAndNavigateToSummary = (orderId: string, pendingReconciliation: boolean = false) => {
    // Clear polling interval BEFORE navigation to prevent continued polling
    if (pollTimer.current) {
      console.log('[ChargingScreen] Clearing polling interval before navigation');
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }

    // End the Live Activity / ongoing notification explicitly here.
    // Navigating to ChargeSummary does NOT unmount ChargingScreen (it stays
    // in the navigation stack), so the unmount cleanup never runs for this
    // poll-detected completion path — without this the Live Activity would
    // stay on the lock screen after the charge finished.
    if (liveActivityStartedRef.current) {
      liveActivityStartedRef.current = false;
      liveActivityInitialized.current = false;
      setLiveActivityStarted(false);

      if (Platform.OS === 'ios') {
        liveActivityService
          .endActivity(
            latestKWhChargedRef.current,
            latestEstPriceRef.current,
            latestLocalTimerRef.current
          )
          .catch((error: Error) => {
            console.warn('[ChargingScreen] Error ending Live Activity on completion:', error);
          });

        unregisterLiveActivityToken(orderId).catch((unregError) => {
          console.warn('[ChargingScreen] Error unregistering Live Activity token:', unregError);
        });
      }

      if (Platform.OS === 'android') {
        androidChargingNotificationService
          .endWithSummary(
            latestKWhChargedRef.current,
            latestEstPriceRef.current,
            latestLocalTimerRef.current
          )
          .catch((error: Error) => {
            console.warn('[ChargingScreen] Error ending Android notification on completion:', error);
          });
      }
    }

    // Capture charger data with multiple fallbacks to prevent null crashes.
    // Priority: local state → Redux selectedCharger → route params
    const chargerFromLocal = localActiveCharge?.charger;
    const chargerForNavigation = chargerFromLocal ?? selectedCharger ?? props.route.params?.charger ?? null;
    const chargerUuid = chargerForNavigation?.uuid;

    // Clear active charges and accumulated session metrics from Redux store
    // when charge completes. Removes the green dot from hamburger icon and
    // the active charge card from menu.
    dispatch(userActions.clearActiveCharges());
    dispatch(chargerActions.clearChargingSessionMetrics());

    if (chargerUuid) {
      dispatch(updateSelectedCharger(chargerUuid));
    }

    // Check if navigation should be suppressed (e.g., when user disabled charger from edit page)
    if (suppressChargeSummaryNavigation) {
      console.log('Suppressing automatic navigation to ChargeSummary - user disabled charger from edit page');
      setLocalActiveCharge(null);
      props.navigation.goBack();
      return;
    }

    if (!chargerForNavigation) {
      console.warn('[ChargingScreen] Cannot navigate to ChargeSummary - charger data not available, navigating back');
      props.navigation.goBack();
      return;
    }

    const endTime = new Date();

    props.navigation.navigate('ChargeSummary', {
      charger: chargerForNavigation,
      startTime: startTime.current.toISOString(),
      endTime: endTime.toISOString(),
      orderId: orderId,
      fromScreen: 'ChargingScreen',
      pendingReconciliation,
    });
  };

  // Poll function for charger status
  // Wrapped in try/catch to prevent any crashes from polling errors
  let now = new Date();
  const pollAndUpdate = async () => {
    // CRITICAL: Early return if no active charge to prevent null reference errors
    if (!localActiveCharge?.order?.id) {
      console.log('[ChargingScreen] pollAndUpdate: No active charge, skipping poll');
      setChargingStatus(ChargingState.UNKNOWN);
      return;
    }

    // Capture the orderId at the start to ensure consistency throughout the function
    const currentOrderId = localActiveCharge.order.id;

    try {
      const activeChargeResponse = await pollChargingStatus(currentOrderId);
      
      // Double-check localActiveCharge hasn't been cleared during the async call
      if (!localActiveCharge) {
        console.log('[ChargingScreen] pollAndUpdate: localActiveCharge cleared during poll');
        return;
      }

      if (isSuccessful(activeChargeResponse.status)) {
        // Safely access payload
        const payload = activeChargeResponse.payload as any;
        if (!payload) {
          setChargingStatus(ChargingState.UNKNOWN);
          return;
        }

        // Case 1: Order is complete or pending reconciliation (early-return shape).
        // The backend returns { orderStatus, pendingReconciliation, reconciliation, message }
        // instead of the normal active charge data when the session has ended.
        if ('pendingReconciliation' in payload || payload.orderStatus === 'PENDING_RECONCILIATION' || payload.orderStatus === 'COMPLETE') {
          console.log('[ChargingScreen] Order no longer active:', {
            orderStatus: payload.orderStatus,
            pendingReconciliation: payload.pendingReconciliation,
          });
          const isPending = payload.pendingReconciliation === true || payload.orderStatus === 'PENDING_RECONCILIATION';
          updateSelectedChargerAndNavigateToSummary(currentOrderId, isPending);
          return;
        }

        // Cases 2/3: Normal active charge data
        setActiveChargeStatus(payload);
        console.log('[ChargingScreen] getActiveChargeData payload', {
          usageData: payload.usageData,
          totalKwh: payload.totalKwh,
          estimatedCost: payload.estimatedCost,
          peakPrice: payload.peakPrice,
          offPeakPrice: payload.offPeakPrice,
          chargerCarData: payload.chargerCarData,
          chargerState: payload.chargerState,
          dataIsStale: payload.dataIsStale,
        });
        
        // Store chargerState for fault info display
        if (payload.chargerState) {
          setCurrentChargerState(payload.chargerState);
        }

        // Update unavailable window state from polling response
        setUnavailWarning(payload.unavailableWarning ?? null);
        setPausedForUnavailable(payload.pausedForUnavailable ?? false);
        setResumesAt(payload.resumesAt ?? null);
        if (payload.driverUnavailableChoice && !driverUnavailChoice) {
          setDriverUnavailChoice(payload.driverUnavailableChoice);
        }
        
        // Check if the message indicates completion
        if (payload.message === 'complete') {
          // Use the captured orderId to avoid any race conditions
          updateSelectedChargerAndNavigateToSummary(currentOrderId);
          return; // Exit the function to stop further checks if charging is done
        }
        
        // Check for faulted status from chargerState first
        const chargerState = payload.chargerState;
        if (chargerState?.connector_status === 'Faulted') {
          setChargingStatus(ChargingState.FAULTED);
          return;
        }
        
        if (payload.chargerCarData) {
          const isVehicleCharging = payload.chargerCarData.vehicle_charging;
          const isVehicleConnected = payload.chargerCarData.vehicle_connected;

          // Use authoritative backend fields for connectivity and staleness.
          // chargerConnected/dataIsStale come from the backend's WiFi check;
          // fall back to chargerCarData.connected for older backend versions.
          const isWifiConnected = payload.chargerConnected ?? (payload.chargerCarData.connected !== false);
          const isStale = payload.dataIsStale === true;
          setChargerWifiConnected(isWifiConnected);
          setDataIsStale(isStale);
          if (payload.connectedChangedTime && !isWifiConnected) {
            setOfflineSince(new Date(payload.connectedChangedTime));
          } else if (isWifiConnected) {
            setOfflineSince(null);
            setLastDataUpdateTime(new Date());
          }

          // Check the scheduled payload. Are we past the scheduled start time?
          let isScheduled = false;
          const startTimeUTC = payload.schedule?.startTime;

          if (startTimeUTC) {
            // Convert the UTC start time to a Date object (the Date object automatically converts to local time)
            const scheduleStartDate = new Date(startTimeUTC);
            now = new Date();

            // If the scheduled start time is in the future, then charging is scheduled to start
            if (scheduleStartDate > now) {
              isScheduled = true;
            }
          }
          if (isScheduled) {
            if (isVehicleConnected) {
              setChargingStatus(ChargingState.SCHEDULED_CONNECTED);
            } else {
              setChargingStatus(ChargingState.SCHEDULED_NOT_CONNECTED);
            }
          } else {
            if (isVehicleCharging && isVehicleConnected) {
              setChargingStatus(ChargingState.CONNECTED_AND_CHARGING);
            } else if (isVehicleConnected === true) {
              setChargingStatus(ChargingState.CONNECTED_AND_NOT_CHARGING);
            } else {
              setChargingStatus(ChargingState.DISCONNECTED);
            }
          }
        } else {
          setChargingStatus(ChargingState.UNKNOWN);
        }
      } else {
        setChargingStatus(ChargingState.UNKNOWN);
      }
    } catch (error) {
      // Catch any errors during polling to prevent crash
      console.error('[ChargingScreen] Error in pollAndUpdate:', error);
      setChargingStatus(ChargingState.UNKNOWN);
    }
  };

  // Latest-closure ref so the focus effect below can poll without going
  // through a stale pollAndUpdate captured on an earlier render.
  const pollAndUpdateRef = useRef(pollAndUpdate);
  pollAndUpdateRef.current = pollAndUpdate;

  useEffect(() => {
    // fetch the active charges
    dispatch(getActiveCharges());
  }, []);

  useEffect(() => {
    if (activeCharges && activeCharges.length > 0) {
      //console.log("orderId is:", props.route.params.order.id);
      const activeCharge = activeCharges?.find(
        (charge) => charge.order.id === props.route.params.order.id
      );
      // console.log('got active charge:', activeCharge);
      if (activeCharge) {
        //console.log("setting active charge:", activeCharge);
        setLocalActiveCharge(activeCharge);
        setIsWaitingForCharge(false);
        setHasCheckedForCharge(true);
      } else if (!hasCheckedForCharge) {
        // Only show error if we've already waited and checked
        notificationService.sendNotification(
          'error',
          'Unable to locate your charge'
        );
        setHasCheckedForCharge(true);
      }

      if (selectedCharger?.can_schedule && activeCharge?.schedule) {
        //set the schedule feature
        const startTime = new Date(activeCharge.schedule.startTime);
        const endTime = new Date(activeCharge.schedule.endTime);
        setScheduleStartTime(startTime);
        setScheduleEndTime(endTime);
        setSelectedMaxKwh(activeCharge.schedule.max_kwh ?? null);
        setScheduleSet(true);
      }
    } else if (!hasCheckedForCharge) {
      setLocalActiveCharge(null);
    }
  }, [activeCharges, hasCheckedForCharge]);

  // Add a delay before allowing charge checking to account for server lag
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsWaitingForCharge(false);
      setHasCheckedForCharge(true);
      // Re-fetch active charges after the delay
      dispatch(getActiveCharges());
    }, 8000); // 8 second delay

    return () => clearTimeout(timer);
  }, []);

  // Set initial waiting state
  useEffect(() => {
    setIsWaitingForCharge(true);
  }, []);

  // Restore charging session metrics from Redux on mount so values survive
  // navigation away/back and mid-session app restarts.
  useEffect(() => {
    const orderId = props.route.params.order?.id;
    if (
      chargingSessionMetrics &&
      chargingSessionMetrics.orderId === orderId &&
      !metricsRestoredFromRedux.current
    ) {
      metricsRestoredFromRedux.current = true;
      baselineTimer.current = chargingSessionMetrics.baselineTimer;
      baselineKwh.current = chargingSessionMetrics.baselineKwh;
      baselinePrice.current = chargingSessionMetrics.baselinePrice;
      lastKnownServerTime.current = chargingSessionMetrics.lastKnownServerTime;
      lastKnownServerKwh.current = chargingSessionMetrics.lastKnownServerKwh;
      lastKnownServerPrice.current = chargingSessionMetrics.lastKnownServerPrice;
      if (chargingSessionMetrics.lastTimer > 0) {
        setLocalTimer(chargingSessionMetrics.lastTimer);
        setTimeElapsed(chargingSessionMetrics.lastTimer);
      }
      if (chargingSessionMetrics.lastKwh > 0) {
        setKWhCharged(chargingSessionMetrics.lastKwh);
      }
      if (chargingSessionMetrics.lastPrice > 0) {
        setEstPrice(chargingSessionMetrics.lastPrice);
      }
      setMetricsHydrated(true);
      console.log('[ChargingScreen] Restored charging metrics from Redux:', {
        baseline: { timer: chargingSessionMetrics.baselineTimer, kwh: chargingSessionMetrics.baselineKwh, price: chargingSessionMetrics.baselinePrice },
        lastValues: { timer: chargingSessionMetrics.lastTimer, kwh: chargingSessionMetrics.lastKwh, price: chargingSessionMetrics.lastPrice },
      });
    }
  }, []);

  // ============================================================================
  // Live Activity Integration (iOS Dynamic Island) & Android Notification
  // ============================================================================
  
  // Start Live Activity (iOS) or Ongoing Notification (Android) when charge data is available
  useEffect(() => {
    const initChargingUI = async () => {
      console.log('[ChargingScreen] initChargingUI called', {
        platform: Platform.OS,
        hasLocalActiveCharge: !!localActiveCharge,
        alreadyInitialized: liveActivityInitialized.current,
      });

      // Only proceed when we have charge data and haven't initialized yet
      if (!localActiveCharge || liveActivityInitialized.current) {
        return;
      }

      liveActivityInitialized.current = true; // Prevent multiple init attempts

      // ========== iOS: Live Activity (Dynamic Island) ==========
      if (Platform.OS === 'ios') {
        console.log('[ChargingScreen] Checking for existing Live Activity...');
        
        // First check if Live Activities are supported at all
        const isSupported = await liveActivityService.checkSupport();
        console.log('[ChargingScreen] Live Activity support check:', isSupported);
        
        if (!isSupported) {
          console.warn('[ChargingScreen] Live Activities not supported on this device');
          return;
        }
        
        // Check if a Live Activity was already started (e.g., from notification handler)
        const existingActivity = await liveActivityService.getCurrentActivityInfo();
        console.log('[ChargingScreen] Existing activity check:', existingActivity);
        
        if (existingActivity?.isActive && existingActivity?.orderId === localActiveCharge.order.id) {
          // Live Activity already running for this order (started by notification)
          console.log('[ChargingScreen] Live Activity already active from notification, connecting to it');
          setLiveActivityStarted(true);
          return;
        }
        
        console.log('[ChargingScreen] No existing Live Activity, starting new one...');
        
        // Get price per kWh (use peak as default display)
        const pricePerKwh = selectedCharger?.financials?.price_kwh_peak ?? 
                           selectedCharger?.financials?.price_kwh_off_peak ?? 
                           0.15;
        
        console.log('[ChargingScreen] Starting Live Activity with params:', {
          chargerName: localActiveCharge.charger.name,
          chargerAddress: localActiveCharge.charger.full_address,
          orderId: localActiveCharge.order.id,
          chargingCapacity: selectedCharger?.charging_capacity ?? 40,
          pricePerKwh,
        });
        
        const result = await liveActivityService.startActivity({
          chargerName: localActiveCharge.charger.name,
          chargerAddress: localActiveCharge.charger.full_address,
          orderId: localActiveCharge.order.id,
          chargingCapacity: selectedCharger?.charging_capacity ?? 40,
          pricePerKwh: pricePerKwh,
        });
        
        console.log('[ChargingScreen] Live Activity startActivity result:', result);
        
        if (result?.started) {
          setLiveActivityStarted(true);
          console.log('[ChargingScreen] Live Activity started successfully, pushToken:', result.pushToken ? 'present' : 'missing');
          
          // Register push token with backend for server-side updates
          if (result.pushToken) {
            try {
              const regResponse = await registerLiveActivityToken(
                result.pushToken, 
                localActiveCharge.order.id
              );
              if (regResponse.payload?.success) {
                console.log('[ChargingScreen] Live Activity token registered with backend');
              } else {
                console.warn('[ChargingScreen] Failed to register token:', regResponse.payload?.message);
              }
            } catch (regError) {
              console.error('[ChargingScreen] Error registering Live Activity token:', regError);
            }
          }
        } else {
          console.warn('[ChargingScreen] Live Activity could not be started - result:', result);
        }
      }
      
      // ========== Android: Ongoing Notification ==========
      if (Platform.OS === 'android') {
        console.log('[ChargingScreen] Starting Android charging notification...');
        
        // Check if notification already exists for this order
        if (androidChargingNotificationService.isNotificationForOrder(localActiveCharge.order.id)) {
          console.log('[ChargingScreen] Android notification already active for this order');
          setLiveActivityStarted(true);
          return;
        }
        
        const started = await androidChargingNotificationService.startNotification({
          chargerName: localActiveCharge.charger.name,
          orderId: localActiveCharge.order.id,
        });
        
        if (started) {
          setLiveActivityStarted(true);
          console.log('[ChargingScreen] Android charging notification started');
        } else {
          console.warn('[ChargingScreen] Failed to start Android charging notification');
        }
      }
    };
    
    initChargingUI();
  }, [localActiveCharge, selectedCharger]);

  // Update Live Activity (iOS) or Notification (Android) when charging data changes
  useEffect(() => {
    if (!liveActivityStarted) return;
    
    const isActivelyCharging = chargingStatus === ChargingState.CONNECTED_AND_CHARGING;
    const chargingRateKw = isActivelyCharging 
      ? calculateChargingRateKw(selectedCharger?.charging_capacity ?? 40) 
      : 0;
    
    // Compute status string based on chargingStatus state
    let statusString = 'Retrieving status...';
    switch (chargingStatus) {
      case ChargingState.CONNECTED_AND_CHARGING:
        statusString = 'Connected and charging';
        break;
      case ChargingState.CONNECTED_AND_NOT_CHARGING:
        statusString = 'Connected, not charging';
        break;
      case ChargingState.DISCONNECTED:
        statusString = 'Not connected to charger';
        break;
      case ChargingState.SCHEDULED_CONNECTED:
        statusString = 'Scheduled and connected';
        break;
      case ChargingState.SCHEDULED_NOT_CONNECTED:
        statusString = 'Scheduled, not connected';
        break;
      case ChargingState.FAULTED:
        statusString = 'Charger Fault';
        break;
    }
    
    const updateParams = {
      kwhCharged: kWhCharged,
      timeElapsedSeconds: localTimer,
      estimatedCost: estPrice,
      isCharging: isActivelyCharging,
      chargingStatus: statusString,
      chargingRateKw: chargingRateKw,
    };
    
    // Update iOS Live Activity
    if (Platform.OS === 'ios') {
      liveActivityService.updateActivity(updateParams);
    }
    
    // Update Android Notification
    if (Platform.OS === 'android') {
      androidChargingNotificationService.updateNotification(updateParams);
    }
  }, [kWhCharged, localTimer, estPrice, chargingStatus, liveActivityStarted]);

  // Use refs to track latest values for cleanup to avoid stale closure issues
  const liveActivityStartedRef = useRef<boolean>(false);
  const latestKWhChargedRef = useRef<number>(0);
  const latestEstPriceRef = useRef<number>(0);
  const latestLocalTimerRef = useRef<number>(0);

  // Keep refs updated with latest values
  useEffect(() => {
    liveActivityStartedRef.current = liveActivityStarted;
  }, [liveActivityStarted]);

  useEffect(() => {
    latestKWhChargedRef.current = kWhCharged;
  }, [kWhCharged]);

  useEffect(() => {
    latestEstPriceRef.current = estPrice;
  }, [estPrice]);

  useEffect(() => {
    latestLocalTimerRef.current = localTimer;
  }, [localTimer]);

  // Cleanup Live Activity / Notification on unmount (safety measure)
  // Uses refs to access latest values and avoid stale closure issues
  useEffect(() => {
    return () => {
      if (liveActivityStartedRef.current) {
        console.log('[ChargingScreen] Component unmounting, ending charging UI');
        if (Platform.OS === 'ios') {
          // Wrap in try/catch to prevent crash if native module has issues
          try {
            liveActivityService.endActivity(
              latestKWhChargedRef.current,
              latestEstPriceRef.current,
              latestLocalTimerRef.current
            ).catch((error: Error) => {
              console.warn('[ChargingScreen] Error ending Live Activity on unmount:', error);
            });
          } catch (error) {
            console.warn('[ChargingScreen] Exception ending Live Activity on unmount:', error);
          }
        }
        if (Platform.OS === 'android') {
          try {
            androidChargingNotificationService.dismissNotification().catch((error: Error) => {
              console.warn('[ChargingScreen] Error dismissing Android notification on unmount:', error);
            });
          } catch (error) {
            console.warn('[ChargingScreen] Exception dismissing Android notification on unmount:', error);
          }
        }
      }
    };
  }, []);

  // Persist charging session metrics to Redux on unmount so they survive
  // navigation away/back. Uses refs to capture latest values at cleanup time.
  useEffect(() => {
    return () => {
      const orderId = props.route.params.order?.id;
      if (orderId && latestLocalTimerRef.current > 0) {
        dispatch(chargerActions.setChargingSessionMetrics({
          orderId,
          baselineTimer: baselineTimer.current,
          baselineKwh: baselineKwh.current,
          baselinePrice: baselinePrice.current,
          lastKnownServerTime: lastKnownServerTime.current,
          lastKnownServerKwh: lastKnownServerKwh.current,
          lastKnownServerPrice: lastKnownServerPrice.current,
          lastTimer: latestLocalTimerRef.current,
          lastKwh: latestKWhChargedRef.current,
          lastPrice: latestEstPriceRef.current,
        }));
        console.log('[ChargingScreen] Saved charging metrics to Redux on unmount');
      }
    };
  }, []);

  useEffect(() => {
    const startPolling = () => {
      // Don't (re)start polling while the screen is blurred — the focus
      // effect below owns polling when the screen regains focus.
      if (!props.navigation.isFocused()) {
        return;
      }
      pollAndUpdate();
      pollTimer.current = setInterval(pollAndUpdate, 5000); //set the polling interval
    };

    const stopPolling = () => {
      if (pollTimer.current) {
        clearInterval(pollTimer.current); // Clear the interval
        pollTimer.current = null;
      }
    };

    stopPolling();
    startPolling();

    return () => stopPolling(); //cleanup when component unmounts
  }, [localActiveCharge]);

  // Handle polling based on focus state
  useFocusEffect(
    useCallback(() => {
      // On (re)focus, bypass the one-minute throttle so the next poll
      // response is applied immediately — coming back to this screen must
      // resync time/kWh/cost from the server right away instead of letting
      // the local counters run from stale (or zeroed) values.
      lastServerUpdate.current = 0;
      pollAndUpdateRef.current();
      if (!pollTimer.current) {
        pollTimer.current = setInterval(() => pollAndUpdateRef.current(), 5000);
      }
      return () => {
        if (pollTimer.current) {
          clearInterval(pollTimer.current); // Clear interval when screen loses focus
          pollTimer.current = null;
        }
        //update the application state if the charge is active
        dispatch(getActiveCharges());
      };
    }, [])
  );

  useEffect(() => {
    if (!activeChargeStatus || !localActiveCharge) {
      return;
    }

    // One-time seed of the elapsed timer from the authoritative charge start
    // time. A remount (opening from the active charge card, reopening the app)
    // starts localTimer at 0; without this the timer visibly "resets" until
    // server usage data catches up.
    if (!metricsHydrated && activeChargeStatus.chargeStartTime) {
      const startMs = new Date(activeChargeStatus.chargeStartTime).getTime();
      if (!Number.isNaN(startMs)) {
        startTime.current = new Date(startMs);
        const elapsedSeconds = Math.max(
          0,
          Math.floor((Date.now() - startMs) / 1000)
        );
        if (elapsedSeconds > localTimer) {
          console.log(
            `[ChargingScreen] Seeding timer from chargeStartTime: ${activeChargeStatus.chargeStartTime} (${elapsedSeconds}s elapsed)`
          );
          setLocalTimer(elapsedSeconds);
          setTimeElapsed(elapsedSeconds);
        }
      }
    }

    if (activeChargeStatus.usageData) {
      const currentTime = Date.now();
      const timeSinceLastUpdate = currentTime - lastServerUpdate.current;
      const oneMinute = 60 * 1000;

      // Apply every poll until we've seeded from real server usage data once
      // (a remount must resync immediately, even if an earlier payload
      // without usageData already un-dimmed the UI); after that, throttle to
      // once per minute.
      if (!hasSeededUsageFromServer.current || timeSinceLastUpdate >= oneMinute) {
        const rawServerTime =
          activeChargeStatus.usageData.peakTimeSeconds +
          activeChargeStatus.usageData.offPeakTimeSeconds;
        const rawServerKwh =
          activeChargeStatus.usageData.peakKwh +
          activeChargeStatus.usageData.offPeakKwh;
        // Free sessions pin the cost to $0 — the backend's estimatedCost /
        // peakPrice / offPeakPrice are computed at base rates and must not
        // resurrect a nonzero cost after a remount.
        const isFreeCharge = isFreeChargeSession(
          activeChargeStatus.pricing_override,
          localActiveCharge?.order
        );
        const rawServerPrice = isFreeCharge
          ? 0
          : activeChargeStatus.estimatedCost ??
            (activeChargeStatus.peakPrice + activeChargeStatus.offPeakPrice);

        // Detect server data reset: if raw server time decreased, the backend
        // likely started a new OCPP transaction (e.g. after fault recovery).
        // Accumulate the previous segment's values into the baseline so the
        // displayed metrics remain continuous for the user.
        if (lastKnownServerTime.current > 0 && rawServerTime < lastKnownServerTime.current) {
          console.log('[ChargingScreen] Server data reset detected — accumulating baseline', {
            previousServerTime: lastKnownServerTime.current,
            newServerTime: rawServerTime,
            currentBaseline: baselineTimer.current,
          });
          baselineTimer.current += lastKnownServerTime.current;
          baselineKwh.current += lastKnownServerKwh.current;
          baselinePrice.current += lastKnownServerPrice.current;
        }

        lastKnownServerTime.current = rawServerTime;
        lastKnownServerKwh.current = rawServerKwh;
        lastKnownServerPrice.current = rawServerPrice;

        const totalPrice = baselinePrice.current + rawServerPrice;
        const totalTime = baselineTimer.current + rawServerTime;
        const totalKwh = baselineKwh.current + rawServerKwh;

        console.log('[ChargingScreen] Server data update', {
          raw: { time: rawServerTime, kwh: rawServerKwh, price: rawServerPrice },
          baseline: { time: baselineTimer.current, kwh: baselineKwh.current, price: baselinePrice.current },
          total: { time: totalTime, kwh: totalKwh, price: totalPrice },
        });

        // Server totals are authoritative. kWh and cost are intentionally
        // decoupled from the timer comparison — the timer may have been
        // seeded from wall-clock start time (>= time spent charging), which
        // previously blocked these updates entirely after a remount and made
        // the counters restart from zero. Math.max keeps the values
        // monotonic so the local per-second estimate never visibly jumps
        // backwards between server syncs. A free session overrides the
        // monotonic guard: any nonzero cost (stale Redux restore, ticks
        // accrued before the override loaded) is corrected back to $0.
        if (isFreeCharge) {
          setEstPrice(0);
        } else {
          setEstPrice((prev) => Math.max(prev, totalPrice));
        }
        setKWhCharged((prev) => Math.max(prev, totalKwh));
        if (localTimer < totalTime) {
          setTimeElapsed(totalTime);
          setLocalTimer(totalTime);
        }
        hasSeededUsageFromServer.current = true;
        lastServerUpdate.current = currentTime;
      } else {
        console.log(
          `[ChargingScreen] Skipping server update, last update was ${Math.round(
            timeSinceLastUpdate / 1000
          )}s ago`
        );
      }
    }

    // The first poll response — with or without usage data — is the moment we
    // stop treating the metrics as "loading" and un-dim the display.
    if (!metricsHydrated) {
      setMetricsHydrated(true);
    }
  }, [activeChargeStatus]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    
    // Only increment when actively charging AND data is fresh.
    // When dataIsStale the charger is offline so the numbers would be fictional.
    if (chargingStatus === ChargingState.CONNECTED_AND_CHARGING && !dataIsStale) {
      interval = setInterval(() => {
        const kWhPerSecond = ((selectedCharger?.charging_capacity ?? 40) * 220) / 3600 / 1000;
        
        const now = new Date();
        const isPeak = checkIsPeak(now, selectedCharger?.peak_selection ?? '');
        const isHourlyBilling = selectedCharger?.billing_type === 'hourly';
        const override = activeChargeStatus?.pricing_override;
        const isFreeCharge = isFreeChargeSession(override, localActiveCharge?.order);

        let rate: number;
        if (isFreeCharge) {
          rate = 0;
        } else if (override) {
          rate = isPeak ? override.effective_price_peak : override.effective_price_off_peak;
        } else if (isHourlyBilling) {
          rate = isPeak
            ? (selectedCharger?.financials?.price_hourly_peak ?? 0)
            : (selectedCharger?.financials?.price_hourly_off_peak ?? 0);
        } else {
          rate = isPeak
            ? (selectedCharger?.financials?.price_kwh_peak ?? 0)
            : (selectedCharger?.financials?.price_kwh_off_peak ?? 0);
        }

        const costPerSecond = isHourlyBilling
          ? rate / 3600
          : rate * kWhPerSecond;
        
        setLocalTimer((prevTimer) => prevTimer + 1);
        setKWhCharged((prevKWh) => prevKWh + kWhPerSecond);
        setEstPrice((prevPrice) => prevPrice + costPerSecond);
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [chargingStatus, dataIsStale, selectedCharger?.charging_capacity, selectedCharger?.peak_selection, selectedCharger?.billing_type, selectedCharger?.financials, activeChargeStatus?.pricing_override, localActiveCharge?.order]);

  // Dim the time/kWh/cost readouts until they've been seeded from real data,
  // so a remount shows "loading" values instead of a hard reset to zero.
  const metricsOpacity = metricsHydrated ? 1 : 0.35;

  let status = 'Connected and charging';
  let image;
  let tintColor;
  let showFaultInfo = false;
  let showNotChargingInfo = false;

  switch (chargingStatus) {
    case ChargingState.CONNECTED_AND_CHARGING:
      status = 'Connected and charging';
      image = activelyChargingIcon;
      tintColor = undefined;
      break;
    case ChargingState.CONNECTED_AND_NOT_CHARGING:
      status = 'Connected, not charging';
      image = connectedNotCharging;
      tintColor = undefined;
      showNotChargingInfo = true;
      break;
    case ChargingState.DISCONNECTED:
      status = 'Not connected to charger';
      image = notConnectedOrCharging;
      tintColor = undefined;
      break;
    case ChargingState.SCHEDULED_CONNECTED:
      status = 'Scheduled and connected';
      image = scheduledChargeIcon;
      tintColor = '#FBD644';
      break;
    case ChargingState.SCHEDULED_NOT_CONNECTED:
      status = 'Scheduled, not connected';
      image = scheduledChargeIcon;
      tintColor = '#F97777';
      break;
    case ChargingState.FAULTED:
      // Use short user message if available, otherwise generic
      status = currentChargerState?.errorInfo?.user_message?.split(' - ')[0] || 'Charger Fault';
      image = notConnectedOrCharging;
      tintColor = '#3B82F6'; // Blue for faulted
      showFaultInfo = true;
      break;
    default:
      status = 'Retrieving status...';
      break;
  }

  // RFID tap-to-charge: per the backend contract (§"Timing"), an
  // `AUTHORIZED` tap fires its push the moment RemoteStart succeeds, which
  // can be a few seconds before the charger sends StartTransaction. Until
  // polling reports an active OCPP transaction (CONNECTED_AND_CHARGING),
  // show "Starting your charge…" so the user doesn't see a generic
  // "Retrieving status..." right after they were told charging started.
  // We also use this for the brief CONNECTED_AND_NOT_CHARGING gap when
  // kWh is still zero — same root cause.
  const isRfidOrder = localActiveCharge?.order?.source === 'rfid_tap';
  if (isRfidOrder) {
    if (
      chargingStatus === ChargingState.UNKNOWN ||
      (chargingStatus === ChargingState.CONNECTED_AND_NOT_CHARGING &&
        (kWhCharged ?? 0) <= 0)
    ) {
      status = 'Starting your charge…';
    }
  }

  return (
    <View style={{ flex: 1, backgroundColor: '#0B1E1D' }}>
      {/* Hamburger Menu - positioned with safe area offset */}
      <View style={[styles.headerContainer, { top: insets.top + 8 }]}>
        <HeaderWithMenu forceHamburger transparent insideSafeArea darkIcon />
      </View>
      <SafeAreaView style={styles.safeArea} edges={['top']}>
        <View style={[styles.containerBgColor, { flex: 1 }]}>
          <Image
            source={require('../../assets/ActiveChargingBackground.jpg')}
            style={{
              width: '100%',
              height: imageHeight,
            }}
            alt="Background Image"
          />
        {/* Car image commented out for now - too busy
        {showCar && (
          <View style={styles.carContainer}>
            <Image
              source={require('../../assets/CarStatic.png')}
              style={styles.carImage}
              alt="car"
            />
          </View>
        )}
        */}
        {/* Location block always rendered */}
        <View
          style={[
            styles.locationBlock,
            { marginTop: 16 },
          ]}
        >
          <Image
            source={require('../../assets/ChargeLocation.png')}
            style={{ width: 40, height: 40 }}
            alt="Location"
          />
          <View style={{ flexShrink: 1 }}>
            <Text className="text-base font-bold text-white">{`${
              localActiveCharge?.charger.name || 'Loading...'
            }`}</Text>
            <Text className="text-sm font-semibold text-white">{`${
              localActiveCharge?.charger.full_address || ''
            }`}</Text>
          </View>
        </View>

        {/* Unavailable window warning / paused banner */}
        {(unavailWarning || pausedForUnavailable) && (
          <UnavailableWarningBanner
            warning={unavailWarning ?? { approaching: false, minutesUntil: 0, unavailableAt: '', availableAgain: '' }}
            pausedForUnavailable={pausedForUnavailable}
            resumesAt={resumesAt}
            currentChoice={driverUnavailChoice}
            onChoiceMade={handleUnavailableChoice}
          />
        )}

        {/* Show loading indicator when waiting for charge data */}
        {isWaitingForCharge && !localActiveCharge && (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#39BEB7" />
            <Text style={styles.loadingText}>Loading your charge data...</Text>
          </View>
        )}

        {/* WiFi Disconnected / Stale Data Warning Banner */}
        {(!chargerWifiConnected || dataIsStale) && (
          <Pressable
            onPress={() => setWifiDisconnectedModalVisible(true)}
            style={styles.wifiBanner}
          >
            <View style={styles.wifiBannerContent}>
              <MaterialIcons name="wifi-off" size={18} color="#FBD644" />
              <Text style={styles.wifiBannerText}>
                {dataIsStale ? 'Charger Offline — Data May Not Be Current' : 'Charger WiFi Disconnected'}
              </Text>
              <MaterialIcons name="info-outline" size={16} color="#FBD644" />
            </View>
            {offlineSince && (
              <Text style={styles.wifiBannerSubtext}>
                Offline since {offlineSince.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </Text>
            )}
            {!offlineSince && lastDataUpdateTime && (
              <Text style={styles.wifiBannerSubtext}>
                Last updated {lastDataUpdateTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </Text>
            )}
          </Pressable>
        )}

        <View onLayout={handleBelowImageLayout} style={{ opacity: (!chargerWifiConnected || dataIsStale) ? 0.5 : 1 }}>
          <View className="relative">
            <View className="relative mt-4">
              <Image
                source={require('../../assets/chargingHistoryBgImage.png')}
                style={{
                  height:
                    Platform.OS === 'web'
                      ? width <= 430
                        ? width * 0.64
                        : 270
                      : 300,
                  width: '100%',
                }}
                resizeMode="cover"
              />
              <View
                className="flex absolute flex-row items-center w-full"
                style={{
                  height: width <= 430 ? width * 0.35 : 150,
                }}
              >
                <View className="flex-1 items-center w-50">
                  <Pressable 
                    onPress={() => {
                      if (showFaultInfo) setFaultModalVisible(true);
                      else if (showNotChargingInfo) setNotChargingModalVisible(true);
                    }}
                    disabled={!showFaultInfo && !showNotChargingInfo}
                    style={{ alignItems: 'center' }}
                  >
                    <View className="relative">
                      <Image
                        source={image}
                        style={{
                          height: 75,
                          width: 75,
                          tintColor: tintColor,
                          marginTop: 15,
                        }}
                      />
                      {(showFaultInfo || showNotChargingInfo) && (
                        <View
                          style={{
                            position: 'absolute',
                            top: 10,
                            right: -8,
                            backgroundColor: showFaultInfo ? '#3B82F6' : '#FBD644',
                            borderRadius: 12,
                            width: 24,
                            height: 24,
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <MaterialIcons name="info" size={16} color="#FFFFFF" />
                        </View>
                      )}
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                      <Text
                        className="text-xs font-normal text-white"
                        style={{
                          textAlign: 'center',
                          flexWrap: 'wrap',
                          maxWidth: 120,
                          marginTop: 0,
                        }}
                        numberOfLines={2}
                      >
                        {status}
                      </Text>
                      {showFaultInfo && (
                        <Text style={{ color: '#3B82F6', fontSize: 10 }}>ⓘ</Text>
                      )}
                      {showNotChargingInfo && (
                        <Text style={{ color: '#FBD644', fontSize: 10 }}>ⓘ</Text>
                      )}
                    </View>
                  </Pressable>
                </View>
                <View className="flex-1 items-center w-50" style={{ opacity: metricsOpacity }}>
                  <View className="relative">
                    <Image
                      source={require('../../assets/timeElapsedIcon.png')}
                      style={{
                        height: 80,
                        width: 80,
                      }}
                    />
                    <Text
                      style={Platform.select({
                        web: {
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                        },
                        default: {
                          position: 'absolute',
                          top: '40%',
                          left: '5%',
                        },
                      })}
                      className="absolute text-sm font-bold text-white"
                    >
                      {!scheduleSet && secondsToHHMMSS(localTimer)}
                    </Text>
                  </View>
                  <Text className="text-xs font-normal text-white">
                    {!scheduleSet && `Time elapsed`}
                  </Text>
                </View>
              </View>
              <View
                className="flex absolute flex-row items-center w-full"
                style={{
                  height: width <= 430 ? width * 0.33 : 140,
                  marginTop: width <= 430 ? width * 0.33 : 140,
                }}
              >
                <View
                  className={
                    Platform.OS === 'web'
                      ? 'items-center flex-1 w-50'
                      : 'items-center flex-1 w-50 mt-8'
                  }
                  style={{ opacity: metricsOpacity }}
                >
                  <View className="flex flex-row gap-3 items-center">
                    <Image
                      source={require('../../assets/totalKwhCharged.png')}
                      style={{
                        height: 60,
                        width: 60,
                      }}
                    />
                    <View>
                      <Animated.Text
                        style={{
                          color: interpolatedColor,
                        }}
                        className="text-2xl font-bold"
                      >
                        {kwhDisplayValue.toFixed(3)}
                      </Animated.Text>
                      <Text className="text-xs font-medium text-white">
                        kWh
                      </Text>
                    </View>
                  </View>
                  <Text className="mt-3 text-xs font-normal text-white">
                    Total charged
                  </Text>
                </View>
                <View
                  className={
                    Platform.OS === 'web'
                      ? 'items-center flex-1 w-50'
                      : 'items-center flex-1 w-50 mt-8'
                  }
                  style={{ opacity: metricsOpacity }}
                >
                  <View className="relative">
                    <Image
                      source={require('../../assets/AccruedCostBackground.png')}
                      style={{
                        height: 75,
                        width: 110,
                      }}
                    />

                    <Animated.Text
                      style={Platform.select({
                        web: {
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translateX(-50%) translateY(-50%)',
                          fontSize: estPrice >= 10 ? 20 : 24, // Smaller font for 4+ digit values ($10.00+)
                          fontWeight: "500",
                          color: interpolatedColor,
                        },
                        default: {
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: [{ translateX: -75 }, { translateY: -12 }], // Center the text (adjusted for proper centering)
                          fontSize: estPrice >= 10 ? 20 : 24, // Smaller font for 4+ digit values ($10.00+)
                          fontWeight: "500",
                          color: interpolatedColor,
                        },
                      })}
                    >
                      {`$${estPrice.toFixed(2)}`}
                    </Animated.Text>
                  </View>
                  <Text className="text-xs font-normal text-white">
                    Est. cost
                  </Text>
                </View>
              </View>
            </View>
            {scheduleSet && (
              <View>
                {/* Instructional Text on Top */}
                <Text
                  style={{
                    fontSize: 12,
                    color: 'white',
                    textAlign: 'center',
                    marginVertical: 4,
                    marginTop: 10,
                  }}
                >
                  Tap below to adjust your schedule or to cancel the scheduled
                  charge.
                </Text>

                {/* Row containing Scheduler and Cancel button */}
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: 10,
                    gap: 12,
                  }}
                >
                  {/* Scheduler takes available space */}
                  <View style={{ flex: 1, maxWidth: 200 }}>
                    <Scheduler
                      selectedCharger={selectedCharger}
                      scheduleDate={scheduleStartTime}
                      setScheduleTimes={setScheduleTimes}
                      clearSchedule={clearSchedule}
                      presetDate={scheduleStartTime}
                    />
                  </View>
                  {/* Cancel button on the right */}
                  <Pressable
                    onPress={() => {
                      setShowCancelLoader(true);
                      handleChargingDone(props.route.params.order.id);
                    }}
                    style={{
                      paddingVertical: 16,
                      paddingHorizontal: 8,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 14,
                        color: 'white',
                        textDecorationLine: 'underline',
                      }}
                    >
                      Cancel
                    </Text>
                  </Pressable>
                </View>
              </View>
            )}
            {!scheduleSet && (
              <View style={styles.stopChargingContainer}>
                <View>
                  <Text
                    className="mt-4 font-medium text-center text-white"
                    style={{ fontSize: 10 }}
                  >
                    To stop charging, unplug the car at any time or slide below.
                  </Text>
                </View>
                <SlideToActivateButton
                  onComplete={async () => {
                    // Simulate a network request
                    await handleChargingDone(props.route.params.order.id);
                  }}
                  backgroundColor={styles.BgColor.backgroundColor} // Optional custom color
                >
                  Stop Charging
                </SlideToActivateButton>
              </View>
            )}
          </View>
        </View>
        </View>

        {/* On clicking cancel button */}
        <LoadingSpinnerOverlay visible={showCancelLoader} />
        
        {/* Fault Detail Modal */}
        {currentChargerState?.errorInfo && (
          <ChargerStatusDetailModal
            visible={faultModalVisible}
            onClose={() => setFaultModalVisible(false)}
            chargerName={localActiveCharge?.charger.name || 'Charger'}
            status="Faulted"
            errorCode={currentChargerState.error_code}
            errorDescription={currentChargerState.errorInfo.description}
            errorUserMessage={currentChargerState.errorInfo.user_message}
            errorSeverity={currentChargerState.errorInfo.severity}
            errorRecommendedAction={currentChargerState.errorInfo.recommended_action}
            vendorId={currentChargerState.vendor_id}
            vendorErrorCode={currentChargerState.vendor_error_code}
          />
        )}

        {/* Not Charging Troubleshooting Modal */}
        <ChargerStatusDetailModal
          visible={notChargingModalVisible}
          onClose={() => setNotChargingModalVisible(false)}
          chargerName={localActiveCharge?.charger.name || 'Charger'}
          status="Vehicle Connected"
          errorUserMessage="Your car is connected but not charging"
          errorSeverity="info"
          errorDescription="This can happen for a few reasons. Most of the time it resolves on its own within a couple of minutes."
          errorRecommendedAction={
            '• The charger may take a moment to recognize your vehicle and begin supplying energy. Wait 1–2 minutes to see if charging starts.\n\n' +
            '• Your vehicle\'s app may have a charging schedule that is preventing the session from starting. Check your vehicle\'s app and verify that it is available to charge.\n\n' +
            '• Try unplugging the connector and plugging it back in to re-establish the connection.'
          }
        />

        {/* WiFi Disconnected Detail Modal */}
        <ChargerStatusDetailModal
          visible={wifiDisconnectedModalVisible}
          onClose={() => setWifiDisconnectedModalVisible(false)}
          chargerName={localActiveCharge?.charger.name || 'Charger'}
          status="Unavailable"
          errorUserMessage="Charger WiFi Disconnected"
          errorSeverity="warning"
          errorDescription="The charger has lost its WiFi connection. Your vehicle is still charging, but real-time usage data (kWh, cost, time) may not update until connectivity is restored."
          errorRecommendedAction={
            '• You can still end your session at any time using the "Stop Charging" slider below.\n\n' +
            '• If you end your session while the charger is offline, your final charge amount will be calculated once it reconnects and you\'ll receive a notification with the total.\n\n' +
            '• The charger usually reconnects on its own. If it stays offline, contact the charger host for assistance.'
          }
        />
      </SafeAreaView>
    </View>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0B1E1D',
  },
  headerContainer: {
    position: 'absolute',
    left: 12,
    zIndex: 100,
  },
  containerBgColor: {
    backgroundColor: '#0B1E1D',
  },
  carContainer: {
    alignItems: 'center',
    width: '100%',
    marginTop: -40, // Float the car up to overlap with background
    zIndex: 10,
  },
  carImage: {
    width: 280,
    height: 90,
  },
  locationBlock: {
    alignSelf: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  stopChargingContainer: {
    paddingBottom: 24,
  },
  BgColor: {
    backgroundColor: '#F75555',
  },
  BgColor1: {
    backgroundColor: '#39BEB780',
  },
  BgColor2: {
    backgroundColor: '#F97777',
  },
  BgColor3: {
    backgroundColor: '#39BEB7',
  },
  TextColor1: {
    color: '#E8E8E9',
  },
  textColor2: {
    color: '#A3A5A6',
  },
  textColor3: {
    color: '#8D8E90',
  },
  line: {
    borderBottomColor: '#636363',
    borderBottomWidth: 1,
    borderStyle: 'dashed',
    marginVertical: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    fontWeight: 'bold',
    color: '#39BEB7',
    textAlign: 'center',
  },
  wifiBanner: {
    backgroundColor: '#292D3399',
    borderWidth: 1,
    borderColor: '#FBD64460',
    borderRadius: 12,
    marginHorizontal: 20,
    marginTop: 10,
    paddingVertical: 8,
    paddingHorizontal: 14,
    alignItems: 'center',
  },
  wifiBannerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  wifiBannerText: {
    color: '#FBD644',
    fontSize: 13,
    fontWeight: '600',
    flex: 1,
  },
  wifiBannerSubtext: {
    color: '#8D8E90',
    fontSize: 11,
    marginTop: 2,
  },
});
